import { AUTH_API_BASE_URL } from '../config/backendEndpoints';
import { authFetch } from './auth/authFetch';
import analytics from './analytics';

const MAX_DISPLAY_PRODUCTS = 2;
const USER_PRODUCTS_PATH = '/products';
const ENTITLED_STATUSES = new Set(['active', 'trialing', 'in_grace_period', 'grace_period']);
const CANCELLED_STATUSES = new Set(['cancelled', 'canceled']);

// Cleeng "stopped" subscriptions keep access through the paid period; find
// the period end wherever the payload puts it. Epoch seconds (number or
// digit-string) and ISO strings are both accepted; anything else is null.
const getPeriodEndMs = subscription => {
  const raw =
    subscription?.billing_cycle?.period_end ??
    subscription?.billing_cycle?.next_billing_at ??
    subscription?.current_period_ends_at ??
    null;
  if (raw == null) return null;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw * 1000;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (/^\d+$/.test(trimmed)) return Number(trimmed) * 1000;
    const parsed = Date.parse(trimmed);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

// Numeric price for ordering plans; missing/unparseable sorts last.
const planPriceAmount = product => {
  const amount = parseFloat(product?.price ?? product?.pricing?.amount ?? '');
  return Number.isFinite(amount) ? amount : Infinity;
};

// Lowest price first, so the subscribe page shows the cheapest plan (typically
// monthly) on the left.
const sortPlansByPriceAsc = products =>
  [...products].sort((a, b) => planPriceAmount(a) - planPriceAmount(b));

function getSubscriptionId(subscription) {
  if (!subscription || typeof subscription !== 'object') {
    return null;
  }

  return (
    (typeof subscription._id === 'string' && subscription._id) ||
    (typeof subscription.id === 'string' && subscription.id) ||
    (typeof subscription.product_id === 'string' && subscription.product_id) ||
    (typeof subscription.offer_id === 'string' && subscription.offer_id) ||
    (typeof subscription.offerId === 'string' && subscription.offerId) ||
    null
  );
}

function isEntitledSubscription(subscription) {
  if (!subscription || typeof subscription !== 'object') {
    return false;
  }

  const status =
    typeof subscription.status === 'string' ? subscription.status.trim().toLowerCase() : null;

  // Some payloads omit status for active records.
  if (!status) {
    return Boolean(getSubscriptionId(subscription));
  }

  // A cancelled (stopped auto-renew) subscription keeps access until its
  // current billing period actually lapses, per Cleeng lifecycle semantics.
  if (CANCELLED_STATUSES.has(status)) {
    const periodEndMs = getPeriodEndMs(subscription);
    return periodEndMs != null && periodEndMs > Date.now();
  }

  return ENTITLED_STATUSES.has(status);
}

function extractSubscriptionCandidates(data) {
  const candidates = [];

  if (data?.subscription && typeof data.subscription === 'object') {
    candidates.push(data.subscription);
  }

  if (Array.isArray(data?.subscriptions)) {
    candidates.push(...data.subscriptions);
  }

  if (Array.isArray(data?.products?.svod)) {
    candidates.push(...data.products.svod);
  }

  return candidates;
}

class SubscriptionService {
  async request(path, { method = 'GET', body } = {}) {
    const url = `${AUTH_API_BASE_URL}/subscriptions${path}`;
    const options = {
      method,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    };

    if (body) {
      options.body = JSON.stringify(body);
    }

    const response = await authFetch(url, options);

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      const error = new Error(errorData?.error || `Subscription API error: ${response.status}`);
      error.status = response.status;
      error.data = errorData;
      throw error;
    }

    if (response.status === 204) {
      return null;
    }

    return response.json();
  }

  async getSubscriptionSummary() {
    const data = await this.request('/summary');
    const products = sortPlansByPriceAsc(data?.data || []).slice(0, MAX_DISPLAY_PRODUCTS);
    return {
      gateway: data?.gateway,
      products,
    };
  }

  async getSubscriptionCatalog() {
    const data = await this.request('/summary');
    return {
      gateway: data?.gateway,
      products: Array.isArray(data?.data) ? data.data : [],
    };
  }

  async getUserProducts() {
    return this.request(USER_PRODUCTS_PATH);
  }

  async getActiveSubscriptions() {
    const data = await this.request(USER_PRODUCTS_PATH);
    return extractSubscriptionCandidates(data).filter(isEntitledSubscription);
  }

  async hasActiveSubscription() {
    try {
      const subscriptions = await this.getActiveSubscriptions();
      return subscriptions.length > 0;
    } catch (error) {
      console.warn('SubscriptionService: failed to evaluate user products entitlement', error);
      return false;
    }
  }

  /**
   * Tri-state entitlement check. Unlike `hasActiveSubscription`, this
   * distinguishes an AUTHORITATIVE answer from an unknown one:
   *   { subscribed, determined }
   * A slow/errored/unreachable entitlement request yields `determined: false`
   * so callers can fail open (let the user browse) and re-check in the
   * background, rather than collapsing "we don't know" into "not subscribed"
   * and flashing the paywall at a real subscriber.
   */
  async getEntitlement() {
    try {
      const data = await this.request(USER_PRODUCTS_PATH);
      const subscribed =
        extractSubscriptionCandidates(data).filter(isEntitledSubscription).length > 0;
      return { subscribed, determined: true };
    } catch (error) {
      // Network error / timeout / 5xx — we could not determine entitlement.
      // (Auth failures are the ProtectedRoute's concern; it runs before this.)
      console.warn('SubscriptionService: entitlement undetermined', error);
      return { subscribed: false, determined: false };
    }
  }

  async createOrder(productId, { currency = 'USD', country = 'US' } = {}) {
    const order = await this.request('/orders', {
      method: 'POST',
      body: { product_id: productId, currency, country },
    });
    // Remember the order's price so `purchase` can report real revenue even
    // when the /finalize response omits the amount (the #1 gap that left
    // revenue/ARPU/MRR blank). Keyed by every id-like field the order exposes.
    const orderId = order?.id ?? order?._id ?? order?.orderId;
    if (orderId) {
      this.pendingOrders = this.pendingOrders || new Map();
      this.pendingOrders.set(String(orderId), {
        value: order?.amount ?? order?.total ?? order?.price ?? order?.value,
        currency: order?.currency ?? currency,
        productId: order?.product_id ?? productId,
        planName: order?.product_name ?? order?.planName,
      });
    }
    // Checkout started (user created an order for a plan). Report the order's
    // actual currency (Cleeng's total), not the request's 'USD' default.
    analytics.trackBeginCheckout({ item_id: productId, currency: order?.currency ?? currency });
    return order;
  }

  async submitPayment({
    orderId,
    paymentMethodData,
    returnUrl,
    origin,
    browserInfo,
    billingAddress,
  }) {
    return this.request('/payments', {
      method: 'POST',
      body: {
        orderId,
        paymentMethodData,
        returnUrl,
        origin,
        ...(browserInfo ? { browserInfo } : {}),
        ...(billingAddress ? { billingAddress } : {}),
      },
    });
  }

  async finalizePayment({ orderId, details }) {
    const result = await this.request('/finalize', {
      method: 'POST',
      body: { orderId, details },
    });
    // Payment captured — the purchase/subscribe conversion. Prefer the amount
    // the finalize response returns, else fall back to the price captured when
    // the order was created, so revenue is never silently empty.
    const remembered = this.pendingOrders?.get(String(orderId)) || {};
    analytics.trackPurchase({
      transactionId: orderId,
      value: result?.amount ?? result?.value ?? remembered.value,
      currency: result?.currency ?? remembered.currency ?? 'USD',
      productId: result?.product_id ?? result?.productId ?? remembered.productId,
      planName: result?.product_name ?? result?.planName ?? remembered.planName,
    });
    this.pendingOrders?.delete(String(orderId));
    return result;
  }

  async getPaymentHistory() {
    return this.request('/history');
  }

  async changePlan({ productId, immediate = false, couponCode = null }) {
    return this.request('/subscribe', {
      method: 'POST',
      body: {
        product_id: productId,
        platform: 'web',
        immediate,
        ...(couponCode ? { coupon_code: couponCode } : {}),
      },
    });
  }

  async cancelSubscription({ subscriptionId = null, reason = null } = {}) {
    return this.request('/cancel', {
      method: 'POST',
      body: {
        ...(subscriptionId ? { subscription_id: subscriptionId } : {}),
        ...(reason ? { reason } : {}),
      },
    });
  }
}

const subscriptionService = new SubscriptionService();
export default subscriptionService;
